import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { generateClaim, fetchCptCodes } from '../services/api';
import CptCodeCombobox from '../components/CptCodeCombobox';
import SourceCodingPanel from '../components/SourceCodingPanel';
import {
  BILLING_COMPONENTS,
  COMPONENT_LABELS,
  COMPONENT_MODIFIERS,
  resolveFacilityComponent,
  resolveLineModifiers,
  codeComponentIssue,
  permitsPricingModifier,
} from '../utils/billing';

const LETTERS = 'ABCDEFGHIJKL';

function createEmptyService(defaultPos, billingComponent) {
  return {
    dateFrom: '',
    dateTo: '',
    pos: defaultPos || '',
    cpt: '',
    // A line added after the component was chosen inherits its pricing modifier straight
    // away, rather than sitting blank until the component is re-selected. No code is on the
    // line yet, so eligibility can't be checked — and an unknown PC/TC indicator is not
    // evidence of ineligibility (the same rule the server applies). If the code turns out
    // not to permit the modifier, the per-line note says so and the server strips it.
    modifiers: resolveLineModifiers('', billingComponent, undefined),
    diagnosisPointers: '',
    charges: '',
    // Box 24F holds the charge for the WHOLE line (units × unit price); the fee schedule and
    // the server's charge check both work per unit. Keeping the unit price alongside the
    // total lets a units change rescale the total instead of silently under-billing.
    unitCharge: '',
    units: '1',
    emergency: false,
    familyPlan: false,
  };
}


function Section({ icon, title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="gc-section">
      <div className="gc-section-header" onClick={() => setOpen(!open)}>
        <div className="gc-section-header-left">
          <div className="gc-section-icon">{icon}</div>
          <h3 className="gc-section-title">{title}</h3>
        </div>
        <div className={`gc-section-toggle ${open ? 'open' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>
      {open && <div className="gc-section-body">{children}</div>}
    </div>
  );
}

// eslint-disable-next-line no-unused-vars -- TODO: cobPriorRemit is passed by GenerateClaim and consumed by CMS1450Form for the COB balance-forward, but is not wired up here yet.
function CMS1500Form({ claim, facility, switchedFromInstitutional = false, onCancel, isEdit, returnTo, cobMode, cobOrder, cobPayer, cobPriorRemit }) {
  const navigate = useNavigate();
  const [activePayer, setActivePayer] = useState(cobMode && cobOrder > 1 ? cobOrder - 1 : 0);
  const [saving, setSaving]   = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Which component(s) this claim bills. Seeded from the claim (itself seeded from the
  // facility at creation), overridable here: a globally-billing center still bills TC-only
  // for a patient whose radiologist bills the 26 separately.
  const [billingComponent, setBillingComponent] = useState(
    () => claim?.billing_component || resolveFacilityComponent(facility)
  );
  // Set once the biller changes the component on an already-priced claim — the existing line
  // charges were pulled for the OLD component and are now wrong. We deliberately don't
  // overwrite them (a biller may have adjusted a charge on purpose); we say so instead.
  const [componentRepriceWarning, setComponentRepriceWarning] = useState(false);

  const isTechnicalClaim = billingComponent === 'technical';
  // Global is the only component that files the interpreting radiologist — there they ARE the
  // rendering provider (837P loop 2310B / box 24J). Everywhere else the field is captured and
  // left off the claim, which is what makes a later TC → global switch a config change.
  const isGlobalClaim = billingComponent === 'global';

  // Default place of service (box 24B) for a line that has none — from the CLAIM's own
  // facility (returned on the encounter detail), not the globally-selected one, which can be
  // a different facility. The selected facility is only the fallback for a claim object
  // without it (e.g. the worklist row, when the detail fetch failed).
  const rawDefaultPos = claim?.facility_place_of_service ?? facility?.placeOfService;
  const defaultPos = rawDefaultPos != null ? String(rawDefaultPos) : '';

  // A self-pay claim a source system declared (the RIS/EMR sync). It never goes to a
  // payer, so the payer-only referring-provider requirement does not apply to it, and at a
  // facility whose own price is the self-pay authority the total must equal that price
  // (self_pay_source_charge). Both mirror the backend; a COB child is never self-pay.
  const isCobChildClaim = Boolean(cobMode && cobOrder > 1);
  const isSourceSelfPay = Boolean(claim?.source_declared_self_pay) && !isCobChildClaim;
  const selfPaySourceCharge = isCobChildClaim ? null : (claim?.self_pay_source_charge ?? null);
  // 2026-10-13 (A5) — what the source said the visit billed, for the coder. Information only.
  const sourceCoding = isCobChildClaim ? null : (claim?.source_coding ?? null);
  const referringRequired = isTechnicalClaim && !isSourceSelfPay;

  // CMS PC/TC indicator per code on this claim, keyed by code. Fetched for whatever codes the
  // lines currently hold — including a claim loaded for edit, whose codes were never picked
  // in this session — so a line that cannot carry the claim's component says so here rather
  // than at transmission time.
  const [pcTcByCode, setPcTcByCode] = useState({});

  const pendingDateRef = useRef('');
  const onDateKeyDown = (value) => (e) => {
    if (e.key !== 'Tab' || !value) return;
    e.preventDefault();
    pendingDateRef.current = value;
    const form = e.currentTarget.closest('form');
    if (!form) return;
    const focusable = Array.from(form.querySelectorAll(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
    ));
    const idx = focusable.indexOf(e.currentTarget);
    const next = e.shiftKey ? focusable[idx - 1] : focusable[idx + 1];
    if (next) next.focus();
  };
  const onDateFocus = (_current, setter) => () => { if (pendingDateRef.current) setter(pendingDateRef.current); pendingDateRef.current = ''; };

  const [formData, setFormData] = useState(() => {
    const p = claim?.patient;
    const fmtDate = (val) => (val ? String(val).slice(0, 10) : '');

    const patientName = p
      ? [p.last_name, p.first_name].filter(Boolean).join(', ')
      : (claim?.patient_name || '');

    const diagnosesArray = Array(12).fill('');
    if (claim?.diagnoses?.length) {
      claim.diagnoses.forEach((dx) => {
        if (dx.sequence >= 1 && dx.sequence <= 12) {
          diagnosesArray[dx.sequence - 1] = dx.icd_code || '';
        }
      });
    }

    // When switching an institutional encounter to CMS-1500, the institutional service
    // lines carry no diagnosis pointers, so default each to the principal diagnosis (the
    // pointer letter A–L by sequence). For a normal professional claim this stays untouched.
    const principalPointer = (() => {
      if (!switchedFromInstitutional || !claim?.diagnoses?.length) return '';
      const sorted = [...claim.diagnoses].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
      const principal = sorted.find((d) => d.diagnosis_type === 'principal') || sorted[0];
      return principal ? String.fromCharCode(64 + (principal.sequence || 1)) : '';
    })();

    // Detailed service_lines from the encounter detail; failing that, the CPT summary on the
    // worklist row (what's left when the detail fetch fails) — the same fallback CMS1450Form has.
    const summaryCodes = claim?.cpt_codes || [];
    const services = claim?.service_lines?.length
      ? claim.service_lines.map((sl) => ({
          dateFrom:          sl.service_date_from ? fmtDate(sl.service_date_from) : '',
          dateTo:            sl.service_date_to   ? fmtDate(sl.service_date_to)   : '',
          pos:               sl.place_of_service  || defaultPos || '',
          cpt:               sl.cpt_hcpcs_code    || '',
          modifiers:         sl.modifiers         || '',
          diagnosisPointers: sl.diagnosis_pointers || principalPointer || '',
          charges:           sl.charges != null   ? String(sl.charges) : '',
          // Recovered from the stored line total, which is units × unit price.
          unitCharge:        sl.charges != null
                               ? String(Number(sl.charges) / (Number(sl.units) > 0 ? Number(sl.units) : 1))
                               : '',
          units:             sl.units   != null   ? String(sl.units)   : '1',
          emergency:         Boolean(sl.is_emergency),
          familyPlan:        false,
        }))
      : summaryCodes.length
        ? summaryCodes.map((code) => ({
            ...createEmptyService(defaultPos, billingComponent),
            dateFrom:          fmtDate(claim?.service_date),
            dateTo:            fmtDate(claim?.service_date),
            cpt:               code,
            diagnosisPointers: principalPointer,
          }))
        : [createEmptyService(defaultPos, billingComponent)];

    const totalCharge = claim?.amount?.toString() ?? '';

    return {
      patient: {
        name:    patientName,
        dob:     fmtDate(p?.date_of_birth),
        sex:     p?.sex            || '',
        address: p?.street_address || '',
        city:    p?.city           || '',
        state:   p?.state          || '',
        zip:     p?.zip            || '',
        phone:   p?.phone          || '',
      },
      claim: {
        illnessDate:          fmtDate(claim?.illness_date)         || '',
        otherDate:            fmtDate(claim?.other_date)           || '',
        otherDateQualifier:   claim?.other_date_qualifier          || '',
        unableToWorkFrom:     fmtDate(claim?.unable_to_work_from)  || '',
        unableToWorkTo:       fmtDate(claim?.unable_to_work_to)    || '',
        hospitalizationFrom:  fmtDate(claim?.hospitalization_from) || '',
        hospitalizationTo:    fmtDate(claim?.hospitalization_to)   || '',
        priorAuthorization:   claim?.prior_authorization           || '',
        // On an institutional→professional switch the claim has no rendering provider —
        // fall back to the attending provider (present ~81% of the time; the rest the
        // biller must fill, enforced on submit below).
        renderingProvider:    claim?.rendering_provider
                                || (switchedFromInstitutional ? claim?.attending_provider : '')
                                || claim?.provider || '',
        renderingProviderNPI: claim?.rendering_provider_npi
                                || (switchedFromInstitutional ? claim?.attending_provider_npi : '')
                                || '',
        referringProvider:    claim?.referring_provider            || '',
        referringProviderNPI: claim?.referring_provider_npi        || '',
        supervisingProvider:    claim?.supervising_provider        || '',
        supervisingProviderNPI: claim?.supervising_provider_npi    || '',
        interpretingProvider:    claim?.interpreting_provider       || '',
        interpretingProviderNPI: claim?.interpreting_provider_npi   || '',
      },
      diagnoses: diagnosesArray,
      services,
      // COB: a secondary/tertiary claim bills the FULL original charges. The prior payer's
      // payment + adjustments ride along in the 837 COB loops and the clearinghouse balances
      // charge = prior paid + adjustments, so the charge must not be reduced to the forwarded
      // balance. amountPaid is 0 on the new claim (prior payments are reported in the COB
      // loop, not as this claim's own payment), so its balance due equals the full charge.
      financial: {
        totalCharge,
        amountPaid: cobMode && cobOrder > 1 ? '0.00' : claim?.amount_paid?.toString() || '0.00',
        balanceDue: cobMode && cobOrder > 1 ? totalCharge : (claim?.balance_due?.toString() || totalCharge),
      },
      signatures: {
        patient:   false,
        insured:   false,
        physician: false,
      },
    };
  });

  const updateField = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const updateDiagnosis = (index, value) => {
    setFormData((prev) => {
      const newDiag = [...prev.diagnoses];
      newDiag[index] = value;
      return { ...prev, diagnoses: newDiag };
    });
  };

  // Box 24F is the charge for the whole line, so units and the charge move together: editing
  // units rescales the total from the line's unit price, and editing the total redefines that
  // unit price. Without this a 2-unit line kept the 1-unit fee-schedule amount and was billed
  // at half its value — the server's charge check compares per unit and never saw it.
  const updateService = (index, field, value) => {
    setFormData((prev) => {
      const newServices = prev.services.map((s, i) => {
        if (i !== index) return s;
        const next = { ...s, [field]: value };
        if (field === 'units') {
          const unit  = parseFloat(s.unitCharge);
          const units = parseFloat(value);
          if (Number.isFinite(unit) && Number.isFinite(units) && units > 0) {
            next.charges = (unit * units).toFixed(2);
          }
        } else if (field === 'charges') {
          const units = parseFloat(s.units);
          const total = parseFloat(value);
          next.unitCharge = Number.isFinite(total) && Number.isFinite(units) && units > 0
            ? String(total / units)
            : '';
        }
        return next;
      });
      return { ...prev, services: newServices };
    });
  };

  // A CPT/HCPCS code was resolved to its fee-schedule charge (picked from the dropdown, or
  // an exact code typed and confirmed on blur). CMS-1500 has no per-line description field,
  // so only the charge auto-fills — on an explicit pick always, on blur only when empty.
  // The charge arrives already matched to the claim's billing component (the combobox is
  // told which one), so a technical claim fills the TC price rather than the global one.
  const handleCptPick = (index, code, { charge }, { explicit }) => {
    setFormData((prev) => {
      const newServices = prev.services.map((s, i) => {
        if (i !== index) return s;
        const next = { ...s, cpt: code };
        if (charge != null && charge !== '' && (explicit || !String(s.charges).trim())) {
          // The fee schedule prices per unit; box 24F wants the whole line.
          const units = parseFloat(s.units);
          next.unitCharge = String(Number(charge));
          next.charges = (Number(charge) * (Number.isFinite(units) && units > 0 ? units : 1)).toFixed(2);
        }
        return next;
      });
      return { ...prev, services: newServices };
    });
    // Re-picking a code re-prices that line for the current component, so it is no longer
    // one of the stale ones the warning refers to.
    if (explicit) setComponentRepriceWarning(false);
  };

  // Resolve the PC/TC indicator for every complete code on the form. Keyed on the sorted code
  // list, so it runs when a line's code settles rather than on every keystroke; short partial
  // codes are ignored until they look like a real one.
  const lineCodeKey = formData.services
    .map((s) => String(s.cpt || '').trim().toUpperCase())
    .filter((c) => c.length >= 5)
    .sort()
    .join(',');

  useEffect(() => {
    const codes = lineCodeKey ? lineCodeKey.split(',') : [];
    if (codes.length === 0) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await fetchCptCodes({ codes, facilityId: facility?.id });
        if (cancelled) return;
        const next = {};
        for (const row of res?.data || []) next[row.code] = row.pc_tc_indicator ?? null;
        setPcTcByCode((prev) => ({ ...prev, ...next }));
      } catch {
        // Reference data is an aid, not a gate — the server validates for real on submit.
      }
    }, 350);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [lineCodeKey, facility?.id]);

  // Everything the component rules have to say about each line, in render order.
  const lineComponentNotes = useMemo(
    () => formData.services.map((s) => {
      const code = String(s.cpt || '').trim().toUpperCase();
      if (!code || !(code in pcTcByCode)) return null;
      return codeComponentIssue(billingComponent, pcTcByCode[code]);
    }),
    [formData.services, pcTcByCode, billingComponent]
  );

  // Switching the component rewrites every line's pricing modifier immediately, so the
  // biller sees the TC/26 that will actually be filed. The server re-applies the same rule
  // on save (utils/billingComponent.js), so this is a preview, not the enforcement point.
  const changeBillingComponent = (next) => {
    setBillingComponent(next);
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map((s) => ({
        ...s,
        // A code that can't carry the modifier (its own PC/TC indicator says so) gets none.
        modifiers: resolveLineModifiers(
          s.modifiers, next, pcTcByCode[String(s.cpt || '').trim().toUpperCase()]
        ),
      })),
    }));
    // Only warn once there is something priced to be stale.
    setComponentRepriceWarning(
      formData.services.some((s) => String(s.charges).trim() && parseFloat(s.charges) > 0)
    );
  };

  // The service-line section clips overflow, so reserve room beneath the lines while a CPT
  // dropdown is open (mirrors the CMS-1450 revenue table behaviour).
  const [openCptMenus, setOpenCptMenus] = useState(0);
  const handleCptMenuOpenChange = (isOpen) =>
    setOpenCptMenus((n) => Math.max(0, n + (isOpen ? 1 : -1)));

  const addService = () => {
    if (formData.services.length < 6) {
      setFormData((prev) => ({
        ...prev,
        services: [...prev.services, createEmptyService(defaultPos, billingComponent)],
      }));
    }
  };

  const removeService = (index) => {
    if (formData.services.length > 1) {
      setFormData((prev) => ({
        ...prev,
        services: prev.services.filter((_, i) => i !== index),
      }));
    }
  };

  const toggleSignature = (key) => {
    setFormData((prev) => ({
      ...prev,
      signatures: { ...prev.signatures, [key]: !prev.signatures[key] },
    }));
  };

  const computedTotalCharge = useMemo(() => {
    return formData.services
      .reduce((sum, s) => sum + (parseFloat(s.charges) || 0), 0)
      .toFixed(2);
  }, [formData.services]);

  const computedBalance = useMemo(() => {
    const total = parseFloat(computedTotalCharge) || 0;
    const paid = parseFloat(formData.financial.amountPaid) || 0;
    return (total - paid).toFixed(2);
  }, [computedTotalCharge, formData.financial.amountPaid]);

  const hasPatientData    = Boolean(claim?.patient);
  const rawInsurances     = claim?.patient?.insurances ?? [];
  const patientProfileId  = claim?.patient?.id ?? claim?.patient?.patient_id ?? claim?.patient_db_id;

  // When the claim's payer is "frozen" — an in-place Change Payer (payer_overridden) OR a
  // rebill/correction (rebilled_from / freq 7) — the billed payer lives on the claim
  // (claim_payers / claim.payers), NOT on the patient's current insurance profile. Show and
  // resubmit that frozen snapshot so the form reflects what will actually be billed instead
  // of the patient's live primary slot (which can differ — e.g. a rebill switched the payer).
  // For every other claim we keep the existing behaviour: derive from patient_insurance.
  const X12_TO_REL = { '18': 'self', '01': 'spouse', '19': 'child', '20': 'employee', '53': 'life partner', G8: 'other' };
  const effectiveInsurances = useMemo(() => {
    if (!claim?.payer_frozen) return rawInsurances;
    const claimPayers = claim?.payers ?? [];
    return [0, 1, 2].map((i) => {
      const pi = rawInsurances[i] ?? null;
      const cp = claimPayers.find((p) => Number(p.payer_order) === i + 1);
      if (!cp || !cp.payer_name) return pi;
      // Overlay the claim's frozen payer onto the patient_insurance display shape so
      // the existing render/save code (which reads insurance_provider/policy_number/…)
      // works unchanged.
      return {
        ...(pi || {}),
        insurance_provider:     cp.payer_name,
        policy_number:          cp.insured_id ?? pi?.policy_number ?? null,
        group_number:           cp.group_number ?? pi?.group_number ?? null,
        group_name:             cp.group_name ?? pi?.group_name ?? null,
        subscriber_name:        cp.insured_name ?? pi?.subscriber_name ?? null,
        subscriber_relation:    X12_TO_REL[cp.insured_relationship] ?? pi?.subscriber_relation ?? null,
        health_plan_id:         cp.health_plan_id ?? pi?.health_plan_id ?? null,
        release_of_info:        cp.release_of_info ?? pi?.release_of_info ?? 'Y',
        assignment_of_benefits: cp.assignment_of_benefits ?? pi?.assignment_of_benefits ?? 'Y',
      };
    });
  }, [claim?.payer_frozen, claim?.payers, rawInsurances]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!claim?.id) {
      setSaveError('No claim ID found. Please return to the worklist and try again.');
      return;
    }

    // A professional claim needs a rendering provider NPI (box 24J). On an
    // institutional→professional switch the attending NPI is missing ~19% of the time and
    // can't be auto-filled, so require it here rather than emit an invalid 837P.
    // A TECHNICAL claim is exempt — the facility is the performing entity and 2310B is
    // omitted entirely; what it needs instead is the ordering provider.
    if (switchedFromInstitutional && !isTechnicalClaim && !formData.claim.renderingProviderNPI?.trim()) {
      setSaveError('A rendering provider NPI is required to bill this payer professionally. Enter it in the Provider section before generating.');
      return;
    }

    // Mirrors the server-side check in ediClaimBuilder.validateClaims — caught here so the
    // biller fixes it on the form rather than at submission time.
    if (referringRequired && !formData.claim.referringProviderNPI?.trim()) {
      setSaveError('A referring (ordering) provider NPI is required on a technical-component claim. Enter it in the Provider section before generating.');
      return;
    }

    // Mirrors the backend /generate guard: the facility's own self-pay price is the total.
    if (selfPaySourceCharge !== null && computedTotalCharge !== Number(selfPaySourceCharge).toFixed(2)) {
      setSaveError(`This self-pay claim must total $${Number(selfPaySourceCharge).toFixed(2)} — the facility's own price for this order. Adjust the service-line charges to match.`);
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const isCobChild = cobMode && cobOrder > 1;
      const cobPayerData = isCobChild ? (() => {
        const ins = rawInsurances[cobOrder - 1];
        return {
          name:                 cobPayer?.payer_name || cobPayer?.insurance_provider || ins?.insurance_provider || null,
          insuredName:          cobPayer?.insured_name || cobPayer?.subscriber_name || ins?.subscriber_name || null,
          insuredId:            cobPayer?.insured_id || cobPayer?.policy_number || ins?.policy_number || null,
          relationship:         (cobPayer?.insured_relationship || cobPayer?.subscriber_relation || ins?.subscriber_relation || 'Self').toLowerCase(),
          groupName:            cobPayer?.group_name || ins?.group_name || null,
          groupNumber:          cobPayer?.group_number || ins?.group_number || null,
          releaseOfInfo:        'Y',
          assignmentOfBenefits: 'Y',
        };
      })() : null;

      const result = await generateClaim(claim.id, {
        // The server refuses (409) a body built by a form other than the one the claim
        // resolves to, instead of misreading it and dropping the lines.
        formType:     'CMS-1500',
        cobMode:      !!cobMode,
        cobOrder:     cobOrder || null,
        cobPayerData: cobPayerData || null,
        billingComponent,
        claim_details: {
          renderingProvider:    formData.claim.renderingProvider,
          renderingProviderNPI: formData.claim.renderingProviderNPI,
          referringProvider:    formData.claim.referringProvider,
          referringProviderNPI: formData.claim.referringProviderNPI,
          supervisingProvider:    formData.claim.supervisingProvider    || null,
          supervisingProviderNPI: formData.claim.supervisingProviderNPI || null,
          interpretingProvider:    formData.claim.interpretingProvider    || null,
          interpretingProviderNPI: formData.claim.interpretingProviderNPI || null,
          illnessDate:          formData.claim.illnessDate          || null,
          otherDate:            formData.claim.otherDate            || null,
          otherDateQualifier:   formData.claim.otherDateQualifier   || null,
          unableToWorkFrom:     formData.claim.unableToWorkFrom     || null,
          unableToWorkTo:       formData.claim.unableToWorkTo       || null,
          hospitalizationFrom:  formData.claim.hospitalizationFrom  || null,
          hospitalizationTo:    formData.claim.hospitalizationTo    || null,
          priorAuthorization:   formData.claim.priorAuthorization   || null,
        },
        diagnoses: formData.diagnoses,
        services:  formData.services,
        payers: [0, 1, 2].map((i) => {
          const ins = effectiveInsurances[i];
          return {
            name:                 ins?.insurance_provider        || null,
            insuredName:          ins?.subscriber_name           || null,
            insuredId:            ins?.policy_number             || null,
            relationship:         (ins?.subscriber_relation || 'Self').toLowerCase(),
            groupName:            ins?.group_name                || null,
            groupNumber:          ins?.group_number              || null,
            releaseOfInfo:        ins?.release_of_info           || 'Y',
            assignmentOfBenefits: ins?.assignment_of_benefits    || 'Y',
          };
        }),
        financial: {
          totalCharge: computedTotalCharge,
          amountPaid:  formData.financial.amountPaid,
          balanceDue:  computedBalance,
        },
      });

      // Navigate to the new child claim's detail page when generating secondary/tertiary
      if (isCobChild && result?.claimId) {
        navigate(`/claims/${result.claimId}`);
      } else {
        navigate(returnTo || '/worklist');
      }
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Failed to generate claim. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const icons = {
    patient: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
      </svg>
    ),
    insurance: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    claim: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    ),
    diagnosis: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
    services: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="18" rx="2" /><line x1="2" y1="9" x2="22" y2="9" /><line x1="9" y1="3" x2="9" y2="21" />
      </svg>
    ),
    financial: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    signature: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* 1. Patient Information */}
      <Section icon={icons.patient} title="Patient Information">
        <div className="gc-grid">
          <div className="gc-field gc-span-2">
            <label className="gc-label">Patient Name</label>
            <input className={`gc-input${hasPatientData ? ' prefilled' : ''}`} type="text" value={formData.patient.name} onChange={(e) => updateField('patient', 'name', e.target.value)} placeholder="Last, First MI" />
          </div>
          <div className="gc-field">
            <label className="gc-label">Date of Birth</label>
            <input className={`gc-input${hasPatientData ? ' prefilled' : ''}`} type="date" value={formData.patient.dob} onChange={(e) => updateField('patient', 'dob', e.target.value)} onKeyDown={onDateKeyDown(formData.patient.dob)} onFocus={onDateFocus(formData.patient.dob, (v) => updateField('patient', 'dob', v))} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Sex</label>
            <select className="gc-select" value={formData.patient.sex} onChange={(e) => updateField('patient', 'sex', e.target.value)}>
              <option value="">Select...</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
              <option value="U">Unknown</option>
            </select>
          </div>
          <div className="gc-field gc-span-2">
            <label className="gc-label">Address</label>
            <input className={`gc-input${hasPatientData ? ' prefilled' : ''}`} type="text" value={formData.patient.address} onChange={(e) => updateField('patient', 'address', e.target.value)} placeholder="Street address" />
          </div>
          <div className="gc-field">
            <label className="gc-label">City</label>
            <input className={`gc-input${hasPatientData ? ' prefilled' : ''}`} type="text" value={formData.patient.city} onChange={(e) => updateField('patient', 'city', e.target.value)} />
          </div>
          <div className="gc-field">
            <label className="gc-label">State</label>
            <input className={`gc-input${hasPatientData ? ' prefilled' : ''}`} type="text" value={formData.patient.state} onChange={(e) => updateField('patient', 'state', e.target.value)} placeholder="TX" maxLength={2} />
          </div>
          <div className="gc-field">
            <label className="gc-label">ZIP</label>
            <input className={`gc-input${hasPatientData ? ' prefilled' : ''}`} type="text" value={formData.patient.zip} onChange={(e) => updateField('patient', 'zip', e.target.value)} placeholder="75201" maxLength={10} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Phone</label>
            <input className={`gc-input${hasPatientData ? ' prefilled' : ''}`} type="tel" value={formData.patient.phone} onChange={(e) => updateField('patient', 'phone', e.target.value)} placeholder="(___) ___-____" />
          </div>
        </div>
      </Section>

      {/* 2. Insurance / Payer Information — read-only; edit via Patient Profile */}
      <Section icon={icons.insurance} title="Insurance / Payer Information">
        <div className="gc-payer-tabs">
          {['Primary (A)', 'Secondary (B)', 'Tertiary (C)'].map((label, i) => (
            <button type="button" key={i} className={`gc-payer-tab ${activePayer === i ? 'active' : ''}`} onClick={() => setActivePayer(i)}>
              {label}
            </button>
          ))}
        </div>
        {(() => {
          const ins = effectiveInsurances[activePayer];
          const slotLabel = ['Primary', 'Secondary', 'Tertiary'][activePayer];
          if (!ins) {
            return (
              <div className="gc-ins-empty">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <p>No {slotLabel.toLowerCase()} insurance on file for this patient.</p>
                {patientProfileId && (
                  <Link to={`/patients/${patientProfileId}`} className="gc-ins-add-btn">
                    + Add {slotLabel} Insurance
                  </Link>
                )}
              </div>
            );
          }
          return (
            <div className="gc-ins-readonly">
              <div className="gc-ins-readonly-header">
                <span className="gc-ins-payer-name">{ins.insurance_provider || '—'}</span>
                {patientProfileId && (
                  <Link to={`/patients/${patientProfileId}`} className="gc-ins-edit-link">
                    Edit in Patient Profile →
                  </Link>
                )}
              </div>
              <div className="gc-ins-detail-grid">
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Member ID</span>
                  <span className="gc-ins-detail-value">{ins.policy_number || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Group Number</span>
                  <span className="gc-ins-detail-value">{ins.group_number || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Group Name</span>
                  <span className="gc-ins-detail-value">{ins.group_name || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Subscriber</span>
                  <span className="gc-ins-detail-value">{ins.subscriber_name || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Relationship</span>
                  <span className="gc-ins-detail-value">{ins.subscriber_relation || '—'}</span>
                </div>
                <div className="gc-ins-detail-item">
                  <span className="gc-ins-detail-label">Effective Date</span>
                  <span className="gc-ins-detail-value">{ins.effective_date ? new Date(ins.effective_date).toLocaleDateString() : '—'}</span>
                </div>
                {(ins.payer_address || ins.payer_city) && (
                  <div className="gc-ins-detail-item gc-ins-span-2">
                    <span className="gc-ins-detail-label">Payer Address</span>
                    <span className="gc-ins-detail-value">
                      {[ins.payer_address, ins.payer_city, ins.payer_state, ins.payer_zip].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Section>

      {/* 3. Claim Details */}
      <Section icon={icons.claim} title="Claim Details">
        <div className="gc-grid">
          <div className="gc-field">
            <label className="gc-label">Date of Illness/Injury</label>
            <input className="gc-input" type="date" value={formData.claim.illnessDate} onChange={(e) => updateField('claim', 'illnessDate', e.target.value)} onKeyDown={onDateKeyDown(formData.claim.illnessDate)} onFocus={onDateFocus(formData.claim.illnessDate, (v) => updateField('claim', 'illnessDate', v))} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Other Date</label>
            <input className="gc-input" type="date" value={formData.claim.otherDate} onChange={(e) => updateField('claim', 'otherDate', e.target.value)} onKeyDown={onDateKeyDown(formData.claim.otherDate)} onFocus={onDateFocus(formData.claim.otherDate, (v) => updateField('claim', 'otherDate', v))} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Other Date Qualifier <span className="gc-label-hint">e.g. 454, 304, 431</span></label>
            <input className="gc-input" type="text" value={formData.claim.otherDateQualifier} onChange={(e) => updateField('claim', 'otherDateQualifier', e.target.value)} placeholder="454" maxLength={3} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Unable to Work From</label>
            <input className="gc-input" type="date" value={formData.claim.unableToWorkFrom} onChange={(e) => updateField('claim', 'unableToWorkFrom', e.target.value)} onKeyDown={onDateKeyDown(formData.claim.unableToWorkFrom)} onFocus={onDateFocus(formData.claim.unableToWorkFrom, (v) => updateField('claim', 'unableToWorkFrom', v))} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Unable to Work To</label>
            <input className="gc-input" type="date" value={formData.claim.unableToWorkTo} onChange={(e) => updateField('claim', 'unableToWorkTo', e.target.value)} onKeyDown={onDateKeyDown(formData.claim.unableToWorkTo)} onFocus={onDateFocus(formData.claim.unableToWorkTo, (v) => updateField('claim', 'unableToWorkTo', v))} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Hospitalization From</label>
            <input className="gc-input" type="date" value={formData.claim.hospitalizationFrom} onChange={(e) => updateField('claim', 'hospitalizationFrom', e.target.value)} onKeyDown={onDateKeyDown(formData.claim.hospitalizationFrom)} onFocus={onDateFocus(formData.claim.hospitalizationFrom, (v) => updateField('claim', 'hospitalizationFrom', v))} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Hospitalization To</label>
            <input className="gc-input" type="date" value={formData.claim.hospitalizationTo} onChange={(e) => updateField('claim', 'hospitalizationTo', e.target.value)} onKeyDown={onDateKeyDown(formData.claim.hospitalizationTo)} onFocus={onDateFocus(formData.claim.hospitalizationTo, (v) => updateField('claim', 'hospitalizationTo', v))} />
          </div>
          <div className="gc-field gc-span-2">
            <label className="gc-label">Prior Authorization Number</label>
            <input className="gc-input" type="text" value={formData.claim.priorAuthorization} onChange={(e) => updateField('claim', 'priorAuthorization', e.target.value)} placeholder="Authorization #" />
          </div>
        </div>

        <hr className="gc-inner-divider" />
        <div className="gc-inner-label">Providers</div>

        {isTechnicalClaim && (
          <p className="gc-provider-note">
            On a technical-component claim the facility is the performing entity, so no
            rendering provider is filed (837P loop 2310B is omitted).
            {isSourceSelfPay ? (
              <> This is a <strong>self-pay</strong> claim that never goes to a payer, so the
              referring/ordering provider is optional — a self-referred study has none.</>
            ) : (
              <> The <strong>referring/ordering provider is required</strong> instead — a diagnostic
              test is always ordered.</>
            )}
          </p>
        )}

        <div className="gc-grid">
          <div className="gc-field gc-span-2">
            <label className="gc-label">
              Rendering Provider
              {isTechnicalClaim && <span className="gc-label-hint">not filed on technical claims</span>}
            </label>
            <input className="gc-input prefilled" type="text" value={formData.claim.renderingProvider} onChange={(e) => updateField('claim', 'renderingProvider', e.target.value)} placeholder={isTechnicalClaim ? 'Not applicable' : 'Provider name'} />
          </div>
          <div className="gc-field">
            <label className="gc-label">Rendering Provider NPI</label>
            <input className="gc-input" type="text" value={formData.claim.renderingProviderNPI} onChange={(e) => updateField('claim', 'renderingProviderNPI', e.target.value)} placeholder="10-digit NPI" maxLength={10} />
          </div>
          <div className="gc-field gc-span-2">
            <label className="gc-label">
              Referring Provider
              {referringRequired && <span className="gc-label-hint">required</span>}
            </label>
            <input className="gc-input" type="text" value={formData.claim.referringProvider} onChange={(e) => updateField('claim', 'referringProvider', e.target.value)} placeholder="Provider name" />
          </div>
          <div className="gc-field">
            <label className="gc-label">
              Referring Provider NPI
              {referringRequired && <span className="gc-label-hint">required</span>}
            </label>
            <input className="gc-input" type="text" value={formData.claim.referringProviderNPI} onChange={(e) => updateField('claim', 'referringProviderNPI', e.target.value)} placeholder="10-digit NPI" maxLength={10} />
          </div>
          {/* 837P loop 2310D. Some payers require a supervising physician on technical
              claims, where supervision is a condition of payment for the test. Optional —
              no payer requirement is known yet, so it is never enforced. */}
          <div className="gc-field gc-span-2">
            <label className="gc-label">
              Supervising Provider
              <span className="gc-label-hint">optional</span>
            </label>
            <input className="gc-input" type="text" value={formData.claim.supervisingProvider} onChange={(e) => updateField('claim', 'supervisingProvider', e.target.value)} placeholder="Provider name" />
          </div>
          <div className="gc-field">
            <label className="gc-label">Supervising Provider NPI</label>
            <input className="gc-input" type="text" value={formData.claim.supervisingProviderNPI} onChange={(e) => updateField('claim', 'supervisingProviderNPI', e.target.value)} placeholder="10-digit NPI" maxLength={10} />
          </div>
          {/* The radiologist who read the study. Recorded on every claim whatever the
              component, but only a GLOBAL claim puts them on the wire: there they become the
              rendering provider in 837P loop 2310B / box 24J. A technical claim bills the
              equipment and the room, so the reader never appears on it. */}
          <div className="gc-field gc-span-2">
            <label className="gc-label">
              Interpreting provider (radiologist)
              <span className="gc-label-hint">
                {isGlobalClaim ? 'billed as the rendering provider' : 'captured, not billed'}
              </span>
            </label>
            <input className="gc-input" type="text" value={formData.claim.interpretingProvider} onChange={(e) => updateField('claim', 'interpretingProvider', e.target.value)} placeholder="Provider name" />
            <p className="gc-field-hint">
              Captured for every billing component. It is emitted on the claim only when the
              component is Global, where the reading radiologist is the rendering provider.
            </p>
          </div>
          <div className="gc-field">
            <label className="gc-label">
              Interpreting Provider NPI
              {isGlobalClaim && <span className="gc-label-hint">required to bill global</span>}
            </label>
            <input className="gc-input" type="text" value={formData.claim.interpretingProviderNPI} onChange={(e) => updateField('claim', 'interpretingProviderNPI', e.target.value)} placeholder="10-digit NPI" maxLength={10} />
          </div>
        </div>
      </Section>

      {/* 4. Diagnoses */}
      <Section icon={icons.diagnosis} title="Diagnoses (ICD-10)" defaultOpen={true}>
        <div className="gc-diag-grid">
          {formData.diagnoses.map((code, i) => (
            <div key={i} className="gc-diag-field">
              <span className="gc-diag-letter">{LETTERS[i]}</span>
              <input
                className={`gc-diag-input${code ? ' prefilled' : ''}`}
                type="text"
                value={code}
                onChange={(e) => updateDiagnosis(i, e.target.value)}
                placeholder="ICD-10"
              />
            </div>
          ))}
        </div>
      </Section>

      {/* 5. Service Lines */}
      <Section icon={icons.services} title="Service Lines">
        <SourceCodingPanel sourceCoding={sourceCoding} />
        {/* Billing component — decides the pricing modifier on every line below and which
            fee-schedule price the CPT lookup offers. Defaults from the facility. */}
        <div className="gc-component-bar">
          <div className="gc-field">
            <label className="gc-label" htmlFor="billing-component">Billing Component</label>
            <select
              id="billing-component"
              className="gc-select"
              value={billingComponent}
              onChange={(e) => changeBillingComponent(e.target.value)}
            >
              {BILLING_COMPONENTS.map((c) => (
                <option key={c} value={c}>{COMPONENT_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <p className="gc-component-note">
            {billingComponent === 'technical' && (
              <>Billing the technical component only — every line is filed with <strong>TC</strong> at the technical price. The reading physician bills the <strong>26</strong> separately.</>
            )}
            {billingComponent === 'professional' && (
              <>Billing the professional component only — every line is filed with <strong>26</strong>. Whoever owns the equipment bills the <strong>TC</strong> separately.</>
            )}
            {billingComponent === 'global' && (
              <>Billing the complete service — no pricing modifier, priced at the global rate.</>
            )}
          </p>
        </div>

        {componentRepriceWarning && (
          <div className="gc-component-warning">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <span>
              Modifiers were updated, but the charges below still reflect the previous
              billing component&apos;s pricing. Re-select each CPT/HCPCS code to pull the
              correct amount, or edit the charges by hand.
            </span>
          </div>
        )}

        <div className={openCptMenus > 0 ? 'gc-cpt-menu-open' : ''}>
        {formData.services.map((svc, i) => (
          <div key={i} className="gc-service-line">
            <div className="gc-service-header">
              <span className="gc-service-num">Service {i + 1}</span>
              <button type="button" className="gc-btn-remove-service" onClick={() => removeService(i)} disabled={formData.services.length <= 1}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </button>
            </div>
            <div className="gc-service-grid">
              <div className="gc-field">
                <label className="gc-label">Date From</label>
                <input className="gc-input" type="date" value={svc.dateFrom} onChange={(e) => updateService(i, 'dateFrom', e.target.value)} onKeyDown={onDateKeyDown(svc.dateFrom)} onFocus={onDateFocus(svc.dateFrom, (v) => updateService(i, 'dateFrom', v))} />
              </div>
              <div className="gc-field">
                <label className="gc-label">Date To</label>
                <input className="gc-input" type="date" value={svc.dateTo} onChange={(e) => updateService(i, 'dateTo', e.target.value)} onKeyDown={onDateKeyDown(svc.dateTo)} onFocus={onDateFocus(svc.dateTo, (v) => updateService(i, 'dateTo', v))} />
              </div>
              <div className="gc-field">
                <label className="gc-label">POS</label>
                <input className="gc-input" type="text" value={svc.pos} onChange={(e) => updateService(i, 'pos', e.target.value)} placeholder="Code" maxLength={2} />
              </div>
              <div className="gc-field">
                <label className="gc-label">CPT / HCPCS</label>
                <CptCodeCombobox
                  code={svc.cpt}
                  facilityId={facility?.id}
                  component={billingComponent}
                  inputClassName="gc-input prefilled"
                  placeholder="Code"
                  onCodeChange={(v) => updateService(i, 'cpt', v)}
                  onPick={(c, vals, opts) => handleCptPick(i, c, vals, opts)}
                  onOpenChange={handleCptMenuOpenChange}
                />
              </div>
              <div className="gc-field">
                <label className="gc-label">
                  Modifiers
                  {COMPONENT_MODIFIERS[billingComponent]
                    && permitsPricingModifier(
                      billingComponent, pcTcByCode[String(svc.cpt || '').trim().toUpperCase()]
                    ) && (
                    <span className="gc-label-hint"> {COMPONENT_MODIFIERS[billingComponent]} applied</span>
                  )}
                </label>
                <input className="gc-input" type="text" value={svc.modifiers} onChange={(e) => updateService(i, 'modifiers', e.target.value)} placeholder="Mod" maxLength={11} />
              </div>
              <div className="gc-field">
                <label className="gc-label">Dx Pointers <span className="gc-label-hint">A-L</span></label>
                <input className="gc-input" type="text" value={svc.diagnosisPointers} onChange={(e) => updateService(i, 'diagnosisPointers', e.target.value)} placeholder="A,B" />
              </div>
              <div className="gc-field">
                <label className="gc-label">Charges ($)</label>
                <input className="gc-input" type="number" step="0.01" min="0" value={svc.charges} onChange={(e) => updateService(i, 'charges', e.target.value)} placeholder="0.00" />
              </div>
              <div className="gc-field">
                <label className="gc-label">Units</label>
                <input className="gc-input" type="number" min="1" value={svc.units} onChange={(e) => updateService(i, 'units', e.target.value)} />
              </div>
              <div className="gc-field">
                <label className="gc-label">Emergency</label>
                <div className="gc-checkbox-field">
                  <input className="gc-checkbox" type="checkbox" id={`emg-${i}`} checked={svc.emergency} onChange={(e) => updateService(i, 'emergency', e.target.checked)} />
                  <label className="gc-checkbox-label" htmlFor={`emg-${i}`}>Yes</label>
                </div>
              </div>
              <div className="gc-field">
                <label className="gc-label">Family Plan</label>
                <div className="gc-checkbox-field">
                  <input className="gc-checkbox" type="checkbox" id={`fp-${i}`} checked={svc.familyPlan} onChange={(e) => updateService(i, 'familyPlan', e.target.checked)} />
                  <label className="gc-checkbox-label" htmlFor={`fp-${i}`}>Yes</label>
                </div>
              </div>
            </div>
            {/* Can this code be billed as this claim's component? An error here is the same
                one the server returns at submit — surfaced now, while it is still fixable. */}
            {lineComponentNotes[i] && (
              <p className={`gc-line-note ${lineComponentNotes[i].level}`}>
                <strong>{svc.cpt}</strong> {lineComponentNotes[i].message}.
              </p>
            )}
          </div>
        ))}
        </div>
        <button type="button" className="gc-btn-add-service" onClick={addService} disabled={formData.services.length >= 6}>
          + Add Service Line
        </button>
      </Section>

      {/* 6. Financial Summary */}
      <Section icon={icons.financial} title="Financial Summary">
        {selfPaySourceCharge !== null && (
          <p className="gc-provider-note">
            Self-pay price from the facility: <strong>${Number(selfPaySourceCharge).toFixed(2)}</strong>.
            The facility&apos;s own price (after packages and discounts) is what this patient owes, so the
            total charge must equal it.
          </p>
        )}
        <div className="gc-financial-row">
          <span className="gc-financial-label">Total Charge</span>
          <input
            className="gc-financial-input gc-total-input"
            type="text"
            value={`$${computedTotalCharge}`}
            readOnly
          />
        </div>
        <div className="gc-financial-row">
          <span className="gc-financial-label">Amount Paid</span>
          <input
            className="gc-financial-input"
            type="number"
            step="0.01"
            min="0"
            value={formData.financial.amountPaid}
            onChange={(e) => updateField('financial', 'amountPaid', e.target.value)}
            placeholder="0.00"
          />
        </div>
        <div className="gc-financial-row gc-total">
          <span className="gc-financial-label gc-total-label">Balance Due</span>
          <input
            className="gc-financial-input gc-total-input"
            type="text"
            value={`$${computedBalance}`}
            readOnly
          />
        </div>
      </Section>

      {/* 7. Signatures */}
      <Section icon={icons.signature} title="Authorization & Signatures" defaultOpen={false}>
        <div className="gc-signature-grid">
          <div className={`gc-signature-card ${formData.signatures.patient ? 'signed' : ''}`} onClick={() => toggleSignature('patient')}>
            <div className="gc-signature-check">
              {formData.signatures.patient && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <div className="gc-signature-info">
              <span className="gc-signature-title">Patient Signature</span>
              <span className="gc-signature-desc">Patient authorizes release of information</span>
            </div>
          </div>
          <div className={`gc-signature-card ${formData.signatures.insured ? 'signed' : ''}`} onClick={() => toggleSignature('insured')}>
            <div className="gc-signature-check">
              {formData.signatures.insured && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <div className="gc-signature-info">
              <span className="gc-signature-title">Insured Signature</span>
              <span className="gc-signature-desc">Insured authorizes payment of benefits</span>
            </div>
          </div>
          <div className={`gc-signature-card ${formData.signatures.physician ? 'signed' : ''}`} onClick={() => toggleSignature('physician')}>
            <div className="gc-signature-check">
              {formData.signatures.physician && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <div className="gc-signature-info">
              <span className="gc-signature-title">Physician Signature</span>
              <span className="gc-signature-desc">Physician certifies services rendered</span>
            </div>
          </div>
        </div>
      </Section>

      {/* Actions */}
      {saveError && (
        <div className="gc-save-error">{saveError}</div>
      )}
      <div className="gc-actions">
        <button type="button" className="gc-btn-cancel" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="button" className="gc-btn-save-draft" onClick={() => console.log('Draft saved:', formData)} disabled={saving}>Save Draft</button>
        <button type="submit" className="gc-btn-submit" disabled={saving}>
          {saving ? (
            <>
              <span className="wl-spinner" />
              Saving…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {isEdit ? (
                  <><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></>
                ) : (
                  <><path d="M22 2L11 13" /><path d="M22 2L15 22l-4-9-9-4z" /></>
                )}
              </svg>
              {isEdit ? 'Update Claim' : 'Generate Claim'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

export default CMS1500Form;
